import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AdSpace from '@/components/AdSpace';
import SubscribeBox from '@/components/SubscribeBox';
import HotTopics from '@/components/HotTopics';
import SEO from '@/components/SEO';
import { getArticles } from '@/lib/api';
import { incrementPageViews } from '@/lib/api';
import { Loader2, ShieldCheck, Eye, Calendar } from 'lucide-react';
import { PostPayPromoCard } from '@/components/PostPayPromoCard';
import { getEasternTodayKey, toEasternDateKey } from '@/lib/storyDates';

const BATCH_SIZE = 12;

const CheckYourClaimsPage = () => {
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await getArticles({ category: 'Check Your Claims', limit: 500 });
        const sorted = [...res.data].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        setAllArticles(sorted);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
    incrementPageViews('claims').catch(() => {});
  }, []);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, allArticles.length));
  }, [allArticles.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const visibleArticles = allArticles.slice(0, visibleCount);
  const hasMore = visibleCount < allArticles.length;
  const todayKey = getEasternTodayKey();
  const totalTodayStories = allArticles.filter((article) => article.published_at && toEasternDateKey(article.published_at) === todayKey).length;
  const visibleTodayStories = visibleArticles.filter((article) => article.published_at && toEasternDateKey(article.published_at) === todayKey).length;
  const showPostPayToday = totalTodayStories > 0 && visibleTodayStories === totalTodayStories;
  const dayGroups = useMemo(() => {
    const groups = [];
    visibleArticles.forEach((article) => {
      const key = article.published_at ? toEasternDateKey(article.published_at) : 'Unknown';
      const existing = groups[groups.length - 1];
      if (!existing || existing.key !== key) {
        groups.push({ key, articles: [] });
      }
      groups[groups.length - 1].articles.push(article);
    });
    return groups;
  }, [visibleArticles]);

  return (
    <div className="min-h-screen bg-black page-enter" data-testid="check-claims-page">
      <SEO 
        title="Check Your Claims"
        description="Check On Yo Peoples TV - Check Your Claims"
      />
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="text-center mb-6 py-6 border border-[#222] bg-[#111] animate-fade-in-up">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="text-yellow-500" size={28} />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              CHECK YOUR <span className="text-yellow-500">CLAIMS</span>
            </h1>
            <ShieldCheck className="text-yellow-500" size={28} />
          </div>
          <p className="text-gray-500 text-sm">Verify the facts, check the claims</p>
        </div>

        {/* Header Ad */}
        <AdSpace type="header" className="mb-6" />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          </div>
        ) : (
          <div>
            {dayGroups.length > 0 ? (
              <>
                {dayGroups.map((group) => (
                <section key={group.key} className="mb-8" data-testid={group.key === todayKey ? 'today-stories-group' : `claims-day-group-${group.key}`}>
                  <div className="mb-3 flex items-center gap-2 border-b border-[#222] py-2">
                    <Calendar size={14} className="text-yellow-500" />
                    <span className="text-sm font-bold text-white">{group.key === todayKey ? 'Today' : group.key}</span>
                    <span className="ml-auto text-[10px] text-gray-600">{group.articles.length} {group.articles.length === 1 ? 'story' : 'stories'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-3 lg:gap-4" data-testid={`claims-day-grid-${group.key}`}>
                  {group.articles.map((article, idx) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug || article.id}`}
                      className={`block group animate-fade-in-up stagger-${Math.min(idx + 1, 8)}`}
                      data-testid={`claims-card-${article.id}`}
                    >
                      <div className="bg-[#111] border border-[#222] hover:border-yellow-500/50 hover-lift transition-all duration-300 overflow-hidden">
                        {article.featured_image && (
                          <div className="aspect-[4/3] overflow-hidden">
                            <img
                              src={article.featured_image}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="p-1 sm:p-2">
                          <h3 className="text-[10px] sm:text-xs md:text-sm text-white font-semibold leading-tight line-clamp-2 group-hover:text-yellow-500 transition-colors">
                            {article.title}
                          </h3>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[8px] text-gray-600 sm:text-[10px]">
                              {article.published_at
                                ? new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : 'Draft'}
                            </span>
                            <span className="flex items-center gap-0.5 text-[8px] text-green-500 sm:text-[10px]">
                              <Eye size={8} />
                              {article.views || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                  {group.key === todayKey && showPostPayToday && <PostPayPromoCard />}
                  </div>
                </section>
                ))}

                {hasMore && (
                  <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="claims-load-more-sentinel">
                    <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 border border-[#222] bg-[#111]">
                <p className="text-gray-500">No Claims content yet.</p>
                <p className="text-gray-600 text-sm mt-2">Add Check Your Claims stories in admin</p>
              </div>
            )}
          </div>
        )}

        {/* Footer Ad */}
        <AdSpace type="footer" className="mt-6" />

        {/* Subscribe */}
        <HotTopics />

        <div className="mt-6 animate-fade-in-up">
          <SubscribeBox />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckYourClaimsPage;
